import { Link } from "react-router-dom";
import { CalendarClock, Coins, Gift, ShoppingCart, Truck } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { STATUS, eur } from "@/lib/api";
import { ClubCard } from "@/components/club/ClubCard";
import { StatusBadge } from "@/components/club/StatusBadge";
import { Seo } from "@/components/Seo";

const TIERS = [
  { key: "bronze", req: "Da subito, alla registrazione", rate: 0.005 },
  { key: "gold", req: "1.000 punti guadagnati nell'anno solare", rate: 0.008 },
  { key: "diamond", req: "5.000 punti guadagnati nell'anno solare", rate: 0.01 },
];

const EARN = [
  { icon: ShoppingCart, title: "Compro da solo", pts: "1 punto ogni 1 €", text: "Disponibile sui negozi affiliati (per ora Amazon): compri tu tramite il nostro link e dichiari l'acquisto. Punti accreditati dopo la verifica." },
  { icon: Truck, title: "Fatelo voi", pts: "2 punti ogni 1 €", text: "Acquistiamo noi e ti spediamo il prodotto. Punti accreditati quando riceviamo il bonifico." },
  { icon: Gift, title: "Marketplace Partner", pts: "1 punto ogni 1 €", text: "Offerte esclusive delle aziende partner con prezzo in base al tuo status." },
];

function TierCard({ t }) {
  const s = STATUS[t.key];
  return (
    <div data-testid={`tier-card-${t.key}`} className={`rounded-3xl border bg-gradient-to-br p-6 ${s.card} ${s.ring}`}>
      <StatusBadge status={t.key} />
      <p className="mt-6 text-xs uppercase tracking-widest text-slate-400">Come si ottiene</p>
      <p className="mt-1 text-white">{t.req}</p>
      {t.key !== "bronze" && <p className="text-sm text-slate-400">oppure acquistalo subito</p>}
      <p className="mt-6 text-xs uppercase tracking-widest text-slate-400">Valore dei punti</p>
      <p className="font-display text-3xl font-bold" style={{ color: s.color }}>1.000 pt = {eur(t.rate * 1000)}</p>
      <p className="text-sm text-slate-400">1 punto = {t.rate.toLocaleString("it-IT", { minimumFractionDigits: 3 })} €</p>
      <p className="mt-6 text-xs uppercase tracking-widest text-slate-400">Marketplace</p>
      <p className="text-sm text-slate-300">Paghi il prezzo {s.label} su tutte le offerte partner.</p>
    </div>
  );
}

export default function Club() {
  const { me } = useMe();
  const year = new Date().getFullYear();
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="club-page">
      <Seo title="Status, punti e premi" description="Come funzionano i punti di Club Prezzi: 1, 2 o 3 punti per euro, valore fino a 10 € ogni 1.000 punti, status Bronze, Gold e Diamond."
        jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: [
          ["Quanti punti guadagno?", "1 punto per euro (prezzo senza IVA) comprando da solo o nel Marketplace partner, 2 punti con Fatelo voi, 3 punti sui prodotti del listino Ingrosso elettronica."],
          ["Quanto valgono i punti?", "Bronze 1.000 punti = 5 €, Gold 1.000 punti = 8 €, Diamond 1.000 punti = 10 €."],
          ["Come divento Gold o Diamond?", "Con 1.000 (Gold) o 5.000 (Diamond) punti guadagnati nell'anno solare, oppure acquistando lo status a 50 € o 100 €. Vale fino al 31/12 dell'anno successivo."],
          ["Come pago?", "Con bonifico bancario istantaneo; i punti possono scalare in tutto o in parte il prezzo."],
        ].map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }} />
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
        <div className="fade-up">
          <p className="font-club text-xs text-amber-400">IL CLUB</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Status, punti e premi</h1>
          <p className="mt-5 max-w-xl text-base text-slate-400 md:text-lg">Ogni acquisto ti fa guadagnare punti. Più punti fai nell'anno, più sale il tuo status e più valgono i tuoi punti quando li usi per pagare.</p>
        </div>
        {me ? <ClubCard me={me} /> : (
          <div className="rounded-3xl border border-white/10 bg-[#12141C] p-8">
            <p className="text-white">Accedi per vedere la tua tessera, il saldo punti e lo status.</p>
            <Link to="/sign-up" data-testid="club-signup-link" className="mt-4 inline-block rounded-full bg-amber-500 px-5 py-2 font-semibold text-black hover:bg-amber-400">Entra nel club</Link>
          </div>
        )}
      </div>

      <h2 className="mt-20 font-display text-base font-semibold uppercase tracking-widest text-slate-400 md:text-lg">I tre livelli</h2>
      <p className="mt-2 text-sm text-slate-400">Non vuoi aspettare? <Link to="/diventa-premium" data-testid="club-buy-status-link" className="font-semibold text-amber-400 hover:underline">Acquista subito lo status Gold o Diamond</Link>.</p>
      <div data-testid="welcome-bonus-banner" className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
        <Gift className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <p><b>Bonus di benvenuto subito spendibile:</b> 300 punti alla registrazione (Bronze), 500 punti quando attivi Gold, 1.000 punti quando attivi Diamond.</p>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-3">{TIERS.map((t) => <TierCard key={t.key} t={t} />)}</div>

      <h2 className="mt-20 font-display text-base font-semibold uppercase tracking-widest text-slate-400 md:text-lg">Come guadagni punti</h2>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {EARN.map(({ icon: Icon, title, pts, text }) => (
          <div key={title} className="rounded-3xl border border-white/10 bg-[#12141C] p-6">
            <Icon className="h-6 w-6 text-amber-400" />
            <p className="mt-4 font-display text-lg font-semibold text-white">{title}</p>
            <p className="font-display text-2xl font-bold text-amber-300">{pts}</p>
            <p className="mt-2 text-sm text-slate-400">{text}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-500">I punti si calcolano sull'importo pagato al netto dell'IVA (22%), spedizioni escluse, arrotondato per difetto all'euro.</p>

      <div className="mt-20 grid gap-5 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-[#12141C] p-8" data-testid="redeem-section">
          <Coins className="h-6 w-6 text-amber-400" />
          <h3 className="mt-4 font-display text-2xl font-semibold text-white">Paga con i punti</h3>
          <p className="mt-3 text-slate-400">Su ogni prodotto trovi il tasto <b className="text-amber-300">"Paga con i punti"</b>: i punti scalano il prezzo in base al valore del tuo status e il resto lo paghi con <b className="text-white">bonifico istantaneo</b>. Se i punti coprono tutto, l'ordine è già pagato.</p>
          <div className="mt-6 rounded-2xl bg-white/[0.03] p-5 text-sm text-slate-300">
            <p className="font-semibold text-white">Esempio con 1.000 punti e un ordine da 100 €</p>
            <ul className="mt-2 space-y-1">
              <li>Bronze: sconto 5 € → bonifico di 95 €</li>
              <li>Gold: sconto 8 € → bonifico di 92 €</li>
              <li>Diamond: sconto 10 € → bonifico di 90 €</li>
            </ul>
          </div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-[#12141C] p-8" data-testid="duration-section">
          <CalendarClock className="h-6 w-6 text-sky-400" />
          <h3 className="mt-4 font-display text-2xl font-semibold text-white">Quanto dura lo status</h3>
          <p className="mt-3 text-slate-400">Lo status si basa sui punti <b className="text-white">guadagnati nell'anno solare</b> (1 gennaio – 31 dicembre), non sul saldo: usare i punti non ti fa perdere il livello.</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li>• Lo passi appena raggiungi la soglia e lo mantieni <b className="text-white">fino al 31/12 dell'anno successivo</b>.</li>
            <li>• Esempio: diventi Gold nel {year} → resti Gold per tutto il {year + 1}.</li>
            <li>• Per mantenerlo anche nel {year + 2} devi rifare i punti necessari durante il {year + 1}; altrimenti scendi al livello corrispondente ai punti fatti.</li>
            <li>• Bronze non scade mai.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
